import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, Briefcase, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

const positions = [
  "Freight Operations Executive",
  "Customs Documentation Specialist",
  "Sales / Business Development",
  "Customer Service Executive",
  "Warehouse Supervisor",
  "Other",
];

const ALLOWED = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_BYTES = 5 * 1024 * 1024;

const schema = z.object({
  name: z.string().min(1, "Full name required").max(120),
  email: z.string().email("Valid email required"),
  phone: z.string().min(4, "Phone required").max(40),
  position: z.string().min(1, "Pick a role"),
  message: z.string().max(2000).optional().or(z.literal("")),
});

const inputCls =
  "rounded-none border-slate-300 bg-white focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500 h-12";
const labelCls = "font-semibold text-xs uppercase tracking-wider text-slate-700";

export default function Careers() {
  const [submitted, setSubmitted] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState("");
  const fileRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { position: positions[0] },
  });

  const position = watch("position");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setCvError("");
    if (!f) return setCvFile(null);
    if (!ALLOWED.includes(f.type)) {
      setCvError("CV must be PDF, DOC or DOCX");
      setCvFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setCvError("File too large (max 5 MB)");
      setCvFile(null);
      return;
    }
    setCvFile(f);
  };

  const onSubmit = async (values) => {
    if (!cvFile) {
      setCvError("Please attach your CV");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("email", values.email);
      fd.append("phone", values.phone);
      fd.append("position", values.position);
      if (values.message) fd.append("message", values.message);
      fd.append("cv", cvFile);

      const res = await fetch(`${API}/careers`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSubmitted(true);
      toast.success("Application received. Our HR team will be in touch.");
      reset({ position: positions[0] });
      setCvFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.message || "Could not submit. Please try again.");
    }
  };

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="bg-slate-50 border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 md:mb-16">
          <div className="md:col-span-7 reveal">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-600 mb-4">
              Careers at JMR Global Logistics
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.02]">
              Build the future of<br />
              <span className="text-slate-400">global freight with us.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end reveal">
            <p className="text-slate-600 text-lg md:text-xl">
              We are growing across India, the United Kingdom, and the Middle East. If you are passionate about logistics, we&apos;d love to hear from you.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-slate-200">
          {/* Left highlight */}
          <div className="lg:col-span-5 bg-blue-900 text-white p-8 md:p-12 relative overflow-hidden">
            <Briefcase className="w-12 h-12 text-sky-400 mb-6" strokeWidth={1.4} />
            <h3 className="font-display font-bold text-2xl md:text-3xl mb-4 leading-tight">
              Apply with your CV in under a minute.
            </h3>
            <p className="text-slate-200 text-base md:text-lg leading-relaxed mb-8">
              Tell us about yourself and attach your CV. Applications are sent directly to our HR team at <span className="text-sky-300">info@jmrgloballogistics.com</span> for review.
            </p>
            <ul className="space-y-3 text-base">
              {[
                "Roles across operations, sales, customs & customer service",
                "Locations: Chennai · London · Middle East",
                "PDF, DOC or DOCX accepted (max 5 MB)",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-sky-400 mt-0.5 shrink-0" strokeWidth={1.8} />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7 bg-white p-8 md:p-12 reveal">
            {submitted ? (
              <div data-testid="career-success" className="h-full flex flex-col justify-center items-start gap-6 max-w-md">
                <div className="w-16 h-16 bg-sky-400 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-blue-950" />
                </div>
                <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-slate-900">
                  Application received.
                </h3>
                <p className="text-slate-600 text-lg">
                  Thanks for applying. Our HR team will review your CV and reach out if there&apos;s a fit.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="bg-blue-900 text-white px-6 py-3 font-semibold hover:bg-blue-800 transition-colors"
                >
                  Submit another application
                </button>
              </div>
            ) : (
              <form
                data-testid="career-form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                encType="multipart/form-data"
              >
                <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">// APPLICATION FORM</p>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-slate-900 mb-8">Tell us about yourself.</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-name" className={labelCls}>Full Name *</Label>
                    <Input data-testid="career-name" id="c-name" {...register("name")} className={`${inputCls} mt-2`} placeholder="Jane Doe" />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-email" className={labelCls}>Email *</Label>
                    <Input data-testid="career-email" id="c-email" type="email" {...register("email")} className={`${inputCls} mt-2`} placeholder="you@email.com" />
                    {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className={labelCls}>Phone *</Label>
                    <Input data-testid="career-phone" id="c-phone" {...register("phone")} className={`${inputCls} mt-2`} placeholder="+91 98 765 43210" />
                    {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-position" className={labelCls}>Position *</Label>
                    <select
                      data-testid="career-position"
                      id="c-position"
                      value={position}
                      onChange={(e) => setValue("position", e.target.value, { shouldValidate: true })}
                      className="mt-2 w-full h-12 border border-slate-300 bg-white px-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    >
                      {positions.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <Label htmlFor="c-message" className={labelCls}>Cover Note (optional)</Label>
                  <Textarea
                    data-testid="career-message"
                    id="c-message"
                    rows={3}
                    {...register("message")}
                    className="rounded-none border-slate-300 bg-white focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500 mt-2"
                    placeholder="A short note about your experience and why you're interested."
                  />
                </div>

                <div className="mt-5">
                  <Label className={labelCls}>Attach CV * <span className="font-normal normal-case text-slate-500">(PDF, DOC, DOCX — max 5 MB)</span></Label>
                  <label className={`mt-2 flex items-center gap-4 border-2 border-dashed ${cvError ? "border-red-400" : cvFile ? "border-sky-500 bg-sky-50/60" : "border-slate-300 hover:border-sky-400"} cursor-pointer p-4 transition-colors`}>
                    <span className="inline-flex items-center justify-center w-12 h-12 bg-blue-900 text-sky-400">
                      {cvFile ? <FileText className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        {cvFile ? cvFile.name : "Click to upload your CV"}
                      </p>
                      <p className="text-sm text-slate-500">
                        {cvFile ? `${(cvFile.size / 1024).toFixed(1)} KB` : "PDF, DOC or DOCX up to 5 MB"}
                      </p>
                    </div>
                    <input
                      ref={fileRef}
                      data-testid="career-cv"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={onFile}
                      className="hidden"
                    />
                  </label>
                  {cvError && <p className="text-xs text-red-600 mt-1">{cvError}</p>}
                </div>

                <button
                  data-testid="career-submit"
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-blue-900 text-white font-semibold px-10 py-4 hover:bg-sky-400 hover:text-blue-950 transition-colors disabled:opacity-60 shadow-[6px_6px_0px_0px_rgba(14,165,233,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[3px_3px_0px_0px_rgba(15,23,42,0.2)]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>Submit Application →</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
